/**
 * Per-(role, table) access controls — the reusable guts of the unified
 * "Accesso ai dati" matrix (ADR-0010 P7/D18/S3 + ADR-0028 N1).
 *
 * Renders, for one role on one table: the READ row-policy
 * (all / own / none / custom predicate), the per-operation WRITE matrix, and the
 * field-mask — persisting via the unchanged `appsApi.upsertPolicy` /
 * `deletePolicy`. The matrix shell passes a `label` (the surface name) and an
 * optional `assistant` node (the inherit/off toggle) for the row's third column.
 *
 * Extracted from the old table-centric `TablePoliciesSection` (now retired) so a
 * ROLE-centric matrix can reuse the exact same controls + the pure, unit-tested
 * helpers below. Operator console — native `<select>` is acceptable here.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Td, Tr } from "@/components/ui/table";
import {
  appsApi,
  type ColumnOut,
  type PolicyMode,
  type PolicyOp,
  type PolicyOut,
  type PolicyPredicate,
  type PolicyUpsert,
} from "@/lib/api-apps";

/** Map a persisted write-op mode to the matrix selector value. */
export function writeSelFrom(
  mode: PolicyMode | null | undefined,
  fallback: string,
): "inherit" | "all" | "own" | "none" {
  if (mode == null) return fallback as "inherit" | "none";
  if (mode === "full") return "all";
  if (mode === "filtered") return "own";
  return "none";
}

/** Map an update selector to its backend mode (NULL = inherit the read policy). */
export function updModeOf(sel: string): PolicyMode | null {
  return sel === "all" ? "full" : sel === "own" ? "filtered" : sel === "none" ? "none" : null;
}

/** Map a delete selector to its backend mode (NULL = the default "none"). */
export function delModeOf(sel: string): PolicyMode | null {
  return sel === "all" ? "full" : sel === "own" ? "filtered" : null;
}

const selectClass =
  "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

/** Field-level grant per column (D18). "visible" is the default (no grant). */
export type ColGrant = "visible" | "hidden" | "read_only";

/** Reduce the per-column grant map to the wire shape: drop "visible" entries,
 *  and return null when nothing is restricted. Pure + unit-tested. */
export function grantsToBody(
  map: Record<string, ColGrant>,
): Record<string, "hidden" | "read_only"> | null {
  const out: Record<string, "hidden" | "read_only"> = {};
  for (const [col, g] of Object.entries(map)) {
    if (g === "hidden" || g === "read_only") out[col] = g;
  }
  return Object.keys(out).length ? out : null;
}

/** Seed the per-column grant map from a persisted policy (keys may be a column
 *  id OR a friendly name — the backend resolves either). */
export function seedGrants(
  columns: ColumnOut[],
  persisted: Record<string, string> | null | undefined,
): Record<string, ColGrant> {
  const cg = persisted ?? {};
  const out: Record<string, ColGrant> = {};
  for (const c of columns) {
    const g = cg[c.id] ?? cg[c.name];
    out[c.id] = g === "hidden" || g === "read_only" ? g : "visible";
  }
  return out;
}

const SYSTEM_PREDICATE_COLUMNS = ["created_by", "updated_by"] as const;
const PRESENCE_OPS = new Set<PolicyOp>(["is_empty", "is_not_empty"]);

/** UI-level access mode — a friendlier projection of the backend policy. */
type AccessMode = "all" | "own" | "none" | "custom";

export function policyToMode(p: PolicyOut | undefined): AccessMode {
  if (!p) return "all"; // no policy = allow-all
  if (p.mode === "full") return "all";
  if (p.mode === "none") return "none";
  const pred = p.predicate;
  if (pred && pred.column === "created_by" && pred.op === "eq" && pred.value === "$me") return "own";
  return "custom";
}

/**
 * One surface's access controls inside the role matrix. `tableId` + `roleId`
 * scope the policy; `label` is the surface name shown left; `assistant` (if
 * given) is the inherit/off toggle rendered as the row's third cell.
 */
export function PolicyControls({
  appId,
  tableId,
  roleId,
  label,
  policy,
  columns,
  disabled,
  assistant,
}: {
  appId: string;
  tableId: string;
  roleId: string;
  label: React.ReactNode;
  policy: PolicyOut | undefined;
  columns: ColumnOut[];
  disabled: boolean;
  assistant?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fillerSpan = assistant !== undefined ? 2 : 1;

  const [forceCustom, setForceCustom] = React.useState(false);
  const mode: AccessMode = forceCustom ? "custom" : policyToMode(policy);

  const initialPred: PolicyPredicate =
    policy?.mode === "filtered" && policy.predicate
      ? policy.predicate
      : { column: "created_by", op: "eq", value: "$me" };
  const [draft, setDraft] = React.useState<PolicyPredicate>(initialPred);
  React.useEffect(() => setDraft(initialPred), [policy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownPred: PolicyPredicate = { column: "created_by", op: "eq", value: "$me" };
  const [insSel, setInsSel] = React.useState<"full" | "none">(policy?.insert_mode ?? "full");
  const [updSel, setUpdSel] = React.useState<"inherit" | "all" | "own" | "none">(
    writeSelFrom(policy?.update_mode, "inherit"),
  );
  const [delSel, setDelSel] = React.useState<"none" | "all" | "own">(
    writeSelFrom(policy?.delete_mode, "none") as "none" | "all" | "own",
  );
  const grantableColumns = columns.filter((c) => c.kind !== "system");
  const [colGrants, setColGrants] = React.useState<Record<string, ColGrant>>(
    () => seedGrants(grantableColumns, policy?.column_grants),
  );
  React.useEffect(() => {
    setInsSel(policy?.insert_mode ?? "full");
    setUpdSel(writeSelFrom(policy?.update_mode, "inherit"));
    setDelSel(writeSelFrom(policy?.delete_mode, "none") as "none" | "all" | "own");
    setColGrants(seedGrants(grantableColumns, policy?.column_grants));
  }, [policy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function writeBody(): Partial<PolicyUpsert> {
    return {
      insert_mode: insSel,
      update_mode: updModeOf(updSel),
      update_predicate: updSel === "own" ? ownPred : null,
      delete_mode: delModeOf(delSel),
      delete_predicate: delSel === "own" ? ownPred : null,
      column_grants: grantsToBody(colGrants),
    };
  }

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "policies"] });
  const upsert = useMutation({
    mutationFn: (body: Omit<PolicyUpsert, "role_id">) =>
      appsApi.upsertPolicy(appId, tableId, { role_id: roleId, ...body }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (policyId: string) => appsApi.deletePolicy(appId, tableId, policyId),
    onSuccess: invalidate,
  });

  function persist(read: { mode: PolicyMode; predicate?: PolicyPredicate | null }) {
    upsert.mutate({ ...read, ...writeBody() });
  }

  function applyMode(next: AccessMode) {
    if (next === "custom") {
      setForceCustom(true);
      return;
    }
    setForceCustom(false);
    if (next === "all") {
      if (policy) remove.mutate(policy.id);
      return;
    }
    if (next === "none") return void persist({ mode: "none" });
    persist({ mode: "filtered", predicate: ownPred });
  }

  function persistWrite(next: Partial<{ ins: typeof insSel; upd: typeof updSel; del: typeof delSel }>) {
    if (next.ins) setInsSel(next.ins);
    if (next.upd) setUpdSel(next.upd);
    if (next.del) setDelSel(next.del);
    const ins = next.ins ?? insSel;
    const upd = next.upd ?? updSel;
    const del = next.del ?? delSel;
    const wb: Partial<PolicyUpsert> = {
      insert_mode: ins,
      update_mode: updModeOf(upd),
      update_predicate: upd === "own" ? ownPred : null,
      delete_mode: delModeOf(del),
      delete_predicate: del === "own" ? ownPred : null,
      column_grants: grantsToBody(colGrants),
    };
    const read = policy
      ? { mode: policy.mode, predicate: policy.predicate }
      : { mode: "filtered" as PolicyMode, predicate: ownPred };
    upsert.mutate({ ...read, ...wb });
  }

  function persistGrants(colId: string, grant: ColGrant) {
    const next = { ...colGrants, [colId]: grant };
    setColGrants(next);
    const read = policy
      ? { mode: policy.mode, predicate: policy.predicate }
      : { mode: "filtered" as PolicyMode, predicate: ownPred };
    upsert.mutate({
      ...read,
      insert_mode: insSel,
      update_mode: updModeOf(updSel),
      update_predicate: updSel === "own" ? ownPred : null,
      delete_mode: delModeOf(delSel),
      delete_predicate: delSel === "own" ? ownPred : null,
      column_grants: grantsToBody(next),
    });
  }

  const busy = upsert.isPending || remove.isPending;
  const userColumns = columns.filter((c) => c.kind !== "system");
  const isPresence = PRESENCE_OPS.has(draft.op);
  const usesMe = draft.value === "$me";

  function setOp(op: PolicyOp) {
    if (PRESENCE_OPS.has(op)) {
      const { value: _drop, ...rest } = draft;
      setDraft({ ...rest, op });
    } else {
      setDraft({ ...draft, op, value: draft.value ?? "$me" });
    }
  }

  return (
    <>
      <Tr>
        <Td className="font-medium">{label}</Td>
        <Td>
          <select
            className={selectClass}
            value={mode}
            disabled={disabled || busy}
            onChange={(e) => applyMode(e.target.value as AccessMode)}
          >
            <option value="all">{t("apps.policyAll", "All rows")}</option>
            <option value="own">{t("apps.policyOwn", "Own rows (created by user)")}</option>
            <option value="none">{t("apps.policyNone", "No rows")}</option>
            <option value="custom">{t("apps.policyCustom", "Custom filter…")}</option>
          </select>
        </Td>
        {assistant !== undefined && <Td className="text-center">{assistant}</Td>}
      </Tr>
      {mode === "custom" && (
        <Tr>
          <Td />
          <Td colSpan={fillerSpan}>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-surface p-2 text-sm">
              <span className="text-muted-foreground">{t("apps.policyWhere", "Show rows where")}</span>
              <select
                className="rounded border border-border bg-background px-2 py-1"
                value={draft.column}
                disabled={disabled}
                onChange={(e) => setDraft({ ...draft, column: e.target.value })}
              >
                <optgroup label={t("apps.systemColumns", "System")}>
                  {SYSTEM_PREDICATE_COLUMNS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </optgroup>
                {userColumns.length > 0 && (
                  <optgroup label={t("apps.userColumns", "Columns")}>
                    {userColumns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <select
                className="rounded border border-border bg-background px-2 py-1"
                value={draft.op}
                disabled={disabled}
                onChange={(e) => setOp(e.target.value as PolicyOp)}
              >
                <option value="eq">{t("apps.policyOpEq", "equals (=)")}</option>
                <option value="neq">{t("apps.policyOpNeq", "is not (≠)")}</option>
                <option value="contains">{t("apps.policyOpContains", "contains")}</option>
                <option value="is_empty">{t("apps.policyOpIsEmpty", "is empty")}</option>
                <option value="is_not_empty">{t("apps.policyOpIsNotEmpty", "is not empty")}</option>
              </select>
              {!isPresence && (
                <>
                  <select
                    className="rounded border border-border bg-background px-2 py-1"
                    value={usesMe ? "$me" : "literal"}
                    disabled={disabled}
                    onChange={(e) =>
                      setDraft({ ...draft, value: e.target.value === "$me" ? "$me" : "" })
                    }
                  >
                    <option value="$me">{t("apps.policyCurrentUser", "current user ($me)")}</option>
                    <option value="literal">{t("apps.policyLiteral", "a fixed value")}</option>
                  </select>
                  {!usesMe && (
                    <input
                      className="rounded border border-border bg-background px-2 py-1"
                      placeholder={t("apps.policyValue", "value")}
                      value={draft.value ?? ""}
                      disabled={disabled}
                      onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                    />
                  )}
                </>
              )}
              <button
                type="button"
                className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-accent-foreground disabled:opacity-50"
                disabled={disabled || busy}
                onClick={() => persist({ mode: "filtered", predicate: draft })}
              >
                {t("apps.apply", "Apply")}
              </button>
            </div>
          </Td>
        </Tr>
      )}
      {policy && (
        <Tr>
          <Td />
          <Td colSpan={fillerSpan}>
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-border/60 bg-surface/50 p-2 text-xs">
              <span className="font-medium text-muted-foreground">{t("apps.policyWrite", "Write")}</span>
              <label className="flex items-center gap-1">
                {t("apps.policyInsert", "Insert")}
                <select
                  className="rounded border border-border bg-background px-1.5 py-0.5"
                  value={insSel}
                  disabled={disabled || busy}
                  onChange={(e) => persistWrite({ ins: e.target.value as "full" | "none" })}
                >
                  <option value="full">{t("apps.policyAllow", "Allow")}</option>
                  <option value="none">{t("apps.policyDeny", "Deny")}</option>
                </select>
              </label>
              <label className="flex items-center gap-1">
                {t("apps.policyUpdate", "Update")}
                <select
                  className="rounded border border-border bg-background px-1.5 py-0.5"
                  value={updSel}
                  disabled={disabled || busy}
                  onChange={(e) => persistWrite({ upd: e.target.value as typeof updSel })}
                >
                  <option value="inherit">{t("apps.policyInherit", "Like read")}</option>
                  <option value="all">{t("apps.policyAll", "All rows")}</option>
                  <option value="own">{t("apps.policyOwn", "Own rows")}</option>
                  <option value="none">{t("apps.policyNone", "No rows")}</option>
                </select>
              </label>
              <label className="flex items-center gap-1">
                {t("apps.policyDelete", "Delete")}
                <select
                  className="rounded border border-border bg-background px-1.5 py-0.5"
                  value={delSel}
                  disabled={disabled || busy}
                  onChange={(e) => persistWrite({ del: e.target.value as typeof delSel })}
                >
                  <option value="none">{t("apps.policyNone", "No rows")}</option>
                  <option value="all">{t("apps.policyAll", "All rows")}</option>
                  <option value="own">{t("apps.policyOwn", "Own rows")}</option>
                </select>
              </label>
            </div>
          </Td>
        </Tr>
      )}
      {policy && grantableColumns.length > 0 && (
        <Tr>
          <Td />
          <Td colSpan={fillerSpan}>
            <div className="rounded-md border border-border/60 bg-surface/50 p-2 text-xs">
              <span className="font-medium text-muted-foreground">{t("apps.policyFields", "Fields")}</span>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
                {grantableColumns.map((c) => (
                  <label key={c.id} className="flex items-center gap-1">
                    {c.name}
                    <select
                      className="rounded border border-border bg-background px-1.5 py-0.5"
                      value={colGrants[c.id] ?? "visible"}
                      disabled={disabled || busy}
                      onChange={(e) => persistGrants(c.id, e.target.value as ColGrant)}
                    >
                      <option value="visible">{t("apps.fieldVisible", "Visible")}</option>
                      <option value="read_only">{t("apps.fieldReadOnly", "Read-only")}</option>
                      <option value="hidden">{t("apps.fieldHidden", "Hidden")}</option>
                    </select>
                  </label>
                ))}
              </div>
            </div>
          </Td>
        </Tr>
      )}
      {(upsert.isError || remove.isError) && (
        <Tr>
          <Td />
          <Td colSpan={fillerSpan}>
            <Alert tone="danger" title={t("apps.policySaveFailed", "Could not save policy")}>
              <span className="font-mono text-xs">
                {((upsert.error || remove.error) as Error).message}
              </span>
            </Alert>
          </Td>
        </Tr>
      )}
    </>
  );
}
